import type { AvailabilityCalendar } from '../availability/types'
import { resolveAvailabilityAt } from '../availability/resolve'
import {
  DEFAULT_TRANSFER_ELIGIBILITY,
  type TransferEligibilityConfig,
  type TransferEligibilityIssue,
  type TransferEligibilityResult,
  type TransferGateSeverity,
} from './transfer'

export type { TransferEligibilityIssue, TransferEligibilityResult } from './transfer'

export type TransferTargetContext = {
  userId: string
  displayName?: string
  /** Derived presence: ACTIVE | IDLE | OFFLINE */
  presence?: string | null
  /** Manual/AHP activity preset id when set. */
  activityPresetId?: string | null
  /** Manual activity pauseNotifications flag. */
  pauseNotifications?: boolean
  /** True when target has an active voice/video call on any device. */
  onActiveCall?: boolean
  /** Session lifecycle state when target is a provider in an active session. */
  sessionLifecycleState?: string | null
  /** Parsed availability calendar from target profile settings. */
  availabilityCalendar?: AvailabilityCalendar | null
}

function severityRank(severity: TransferGateSeverity): number {
  if (severity === 'block') return 2
  if (severity === 'warn') return 1
  return 0
}

function pushIssue(
  issues: TransferEligibilityIssue[],
  code: string,
  severity: Exclude<TransferGateSeverity, 'allow'>,
  message: string,
): void {
  issues.push({ code, severity, message })
}

function gateFor(
  config: TransferEligibilityConfig,
  key: keyof TransferEligibilityConfig['gates'],
  subKey?: string,
): TransferGateSeverity {
  const gates = config.gates
  if (key === 'activityPreset' && subKey) {
    return gates.activityPreset[subKey] ?? 'allow'
  }
  if (key === 'sessionLifecycle' && subKey) {
    return gates.sessionLifecycle[subKey] ?? 'allow'
  }
  const value = gates[key]
  return typeof value === 'string' ? value : 'allow'
}

function applyGate(
  issues: TransferEligibilityIssue[],
  code: string,
  severity: TransferGateSeverity,
  message: string,
): void {
  if (severity === 'allow') return
  pushIssue(issues, code, severity, message)
}

/**
 * Evaluate whether a transfer target is eligible. Returns aggregated block/warn
 * issues; `allowed` is false when any issue is `block`.
 */
export function evaluateTransferEligibility(
  target: TransferTargetContext,
  config: TransferEligibilityConfig = DEFAULT_TRANSFER_ELIGIBILITY,
): TransferEligibilityResult {
  const issues: TransferEligibilityIssue[] = []
  const name = target.displayName?.trim() || 'This user'

  if (target.onActiveCall) {
    applyGate(
      issues,
      'target_on_active_call',
      gateFor(config, 'targetOnActiveCall'),
      `${name} is already on a call.`,
    )
  }

  if (target.presence === 'OFFLINE') {
    applyGate(
      issues,
      'target_offline',
      gateFor(config, 'targetOffline'),
      `${name} appears offline.`,
    )
  }

  if (target.pauseNotifications) {
    applyGate(
      issues,
      'target_pause_notifications',
      gateFor(config, 'targetPauseNotifications'),
      `${name} has notifications paused (do not disturb).`,
    )
  }

  if (target.activityPresetId) {
    applyGate(
      issues,
      `activity_${target.activityPresetId}`,
      gateFor(config, 'activityPreset', target.activityPresetId),
      `${name} is marked ${target.activityPresetId.replace(/-/g, ' ')}.`,
    )
  }

  if (target.availabilityCalendar) {
    const resolved = resolveAvailabilityAt(target.availabilityCalendar)
    if (resolved === 'unavailable') {
      applyGate(
        issues,
        'target_calendar_unavailable',
        gateFor(config, 'targetCalendarUnavailable'),
        `${name} is marked unavailable on their calendar.`,
      )
    }
  }

  const lifecycle = target.sessionLifecycleState?.trim()
  if (lifecycle) {
    applyGate(
      issues,
      `session_${lifecycle}`,
      gateFor(config, 'sessionLifecycle', lifecycle),
      `${name} is in an active session (${lifecycle.replace(/-/g, ' ')}).`,
    )
  } else if (target.sessionLifecycleState === '') {
    // treat empty string as no session
  } else {
    // no-op
  }

  const blocked = issues.some((i) => i.severity === 'block')
  return { allowed: !blocked, issues }
}

/** Pick the highest-severity issue for toast/sheet headline copy. */
export function primaryTransferEligibilityIssue(
  result: TransferEligibilityResult,
): TransferEligibilityIssue | null {
  if (result.issues.length === 0) return null
  return [...result.issues].sort((a, b) => severityRank(b.severity) - severityRank(a.severity))[0]
}
